import { useState, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isDefined, getDealerAddress, DealerLocationPartToExcludeEnum } from "@perxis-auto/shared";
import { useGetDealershipQuery, useGetCityQuery } from "../../../entities/dealer/api/api.js";
import "@perxis-auto/shared/lib";
import { useMergedConstructorItemProps } from "../../../shared/lib/constructor/hooks/useMergedConstructorItemProps.js";
import "../../../shared/lib/constructor/mergeConstructor.js";
import "react/jsx-runtime";
import "@perxis-auto/ui-kit";
import { formatDealershipPhones } from "../../../shared/lib/format/dealership/formatDealershipPhones.js";
import { formatDealershipWorkTime } from "../../../shared/lib/format/dealership/formatDealershipWorkTime.js";
import { APP } from "../../../shared/config/app.js";
import "../../../shared/config/env.js";
import "../../../shared/config/events.js";
import { formatVehicleDealership } from "../../../shared/lib/format/vehicle/formatVehicleDealership.js";
import "../../../shared/lib/logger/index.js";
import "react-router";
import "@perxis-auto/shared/lib/lib/is/isObject";
import "../../../shared/lib/store/createAppApi.js";
import "../../../shared/lib/store/createAppAsyncThunk.js";
import "../../../shared/lib/store/createAppSelector.js";
import "../../../shared/lib/store/createAppSlice.js";
import "redux";
import "../../../shared/lib/store/hooks/useAppDispatch.js";
import { useAppSelector } from "../../../shared/lib/store/hooks/useAppSelector.js";
import "../../../shared/lib/store/hooks/useAppStore.js";
import "../../../entities/dealer/model/selectors/selectCitiesParameters.js";
import { selectCityParameters } from "../../../entities/dealer/model/selectors/selectCityParameters.js";
import { selectDealershipParameters } from "../../../entities/dealer/model/selectors/selectDealershipParameters.js";
import "../../../shared/context/constructor.js";
import { useThisContext } from "../../../shared/context/this.js";
import "../../../shared/store/config/middleware/index.js";
import "../../../shared/store/config/model/selectors/selectBenefits.js";
import "../../../shared/store/config/model/selectors/selectBooking.js";
import "../../../shared/store/config/model/selectors/selectCredit.js";
import "../../../shared/store/config/model/selectors/selectFilterGrid.js";
import "../../../shared/store/config/model/selectors/selectFilterGridItems.js";
import "../../../shared/store/config/model/selectors/selectFilters.js";
import "../../../shared/store/config/model/selectors/selectGeolocator.js";
import "../../../shared/store/config/model/selectors/selectLocationCollectionIds.js";
import { selectMaps } from "../../../shared/store/config/model/selectors/selectMaps.js";
import "../../../shared/store/config/model/selectors/selectModels.js";
import "../../../shared/store/config/model/selectors/selectSort.js";
import "../../../shared/store/config/model/selectors/selectTags.js";
import "../../../shared/store/config/model/selectors/selectTradeIn.js";
import "../../../shared/store/config/model/slice.js";
import { GET_DEFAULT_CONTAINER_PROPS } from "../config/props.js";
import { useOccupiedAreas } from "../lib/useOccupiedAreas.js";
const useContainer = (item) => {
  var _a, _b, _c, _d;
  const props = useMergedConstructorItemProps(item, GET_DEFAULT_CONTAINER_PROPS);
  const { t } = useTranslation(APP.name);
  const vehicleItem = useThisContext();
  const dealershipId = vehicleItem ? (_a = formatVehicleDealership(vehicleItem)) == null ? void 0 : _a.id : void 0;
  const dealershipParameters = useAppSelector((state) => selectDealershipParameters(state, dealershipId));
  const { currentData: dealershipItem } = useGetDealershipQuery(dealershipParameters, {
    skip: !vehicleItem || !dealershipId
  });
  const cityParameters = useAppSelector(
    (state) => {
      var _a2;
      return selectCityParameters(state, (_a2 = dealershipItem == null ? void 0 : dealershipItem.data.address.city) == null ? void 0 : _a2.id);
    }
  );
  const { currentData: cityItem } = useGetCityQuery(cityParameters, {
    skip: !((_b = dealershipItem == null ? void 0 : dealershipItem.data.address.city) == null ? void 0 : _b.id)
  });
  const [containerRef, setContainerRef] = useState(null);
  const occupiedAreas = useOccupiedAreas(containerRef);
  const mapRef = useRef(null);
  const bodyRef = useCallback((node) => setContainerRef(node), []);
  const {
    key: mapsKey,
    provider: mapsProvider,
    placemark,
    clustering,
    styles
  } = useAppSelector(selectMaps);
  const { address } = (dealershipItem == null ? void 0 : dealershipItem.data) || {};
  const isHidden = !dealershipItem || !isDefined(address == null ? void 0 : address.latitude) || !isDefined(address == null ? void 0 : address.longitude);
  const zoomControlPosition = {
    right: 18,
    top: ((_c = mapRef == null ? void 0 : mapRef.current) == null ? void 0 : _c.offsetHeight) ? (mapRef.current.offsetHeight - 46) / 2 : 140
  };
  const location = useMemo(() => {
    var _a2;
    const clusterName = (_a2 = cityItem == null ? void 0 : cityItem.data.cluster) == null ? void 0 : _a2.displayName;
    return {
      name: (dealershipItem == null ? void 0 : dealershipItem.data.alternateName) || (dealershipItem == null ? void 0 : dealershipItem.data.name) || "",
      city: clusterName || (cityItem == null ? void 0 : cityItem.data.alternateName) || (cityItem == null ? void 0 : cityItem.data.name) || "",
      address: address ? getDealerAddress(address, !clusterName ? DealerLocationPartToExcludeEnum.City : void 0) : ""
    };
  }, [
    (_d = cityItem == null ? void 0 : cityItem.data.cluster) == null ? void 0 : _d.displayName,
    cityItem == null ? void 0 : cityItem.data.alternateName,
    cityItem == null ? void 0 : cityItem.data.name,
    dealershipItem == null ? void 0 : dealershipItem.data.alternateName,
    dealershipItem == null ? void 0 : dealershipItem.data.name,
    address
  ]);
  const contact = useMemo(
    () => ({
      phone: {
        label: t("widgets.dealerMap.phoneLabel"),
        value: !dealershipItem ? [] : formatDealershipPhones(dealershipItem)
      },
      schedule: {
        label: t("widgets.dealerMap.scheduleLabel"),
        value: !dealershipItem ? [] : formatDealershipWorkTime(dealershipItem)
      }
    }),
    [dealershipItem, t]
  );
  const pins = useMemo(
    () => dealershipItem ? [
      {
        id: dealershipItem.id,
        // @ts-ignore
        latitude: Number(address.latitude),
        // @ts-ignore
        longitude: Number(address.longitude),
        classes: ""
      }
    ] : [],
    // @ts-ignore
    [address == null ? void 0 : address.latitude, address == null ? void 0 : address.longitude, dealershipItem]
  );
  const {
    enable: enableClustering,
    image: clusterImage,
    imageHover: clusterHoverImage,
    offset: clusterOffset,
    offsetHover: clusterHoverOffset,
    size: clusterSize,
    sizeHover: clusterHoverSize
  } = clustering || {};
  const {
    image: placemarkImage,
    imageHover: placemarkHoverImage,
    imageActive: placemarkActiveImage,
    offset: placemarkOffset,
    offsetHover: placemarkHoverOffset,
    offsetActive: placemarkActiveOffset,
    size: placemarkSize,
    sizeHover: placemarkHoverSize,
    sizeActive: placemarkActiveSize
  } = placemark || {};
  return {
    ...props,
    isHidden,
    location,
    contact,
    mapsKey,
    mapsProvider,
    enableClustering,
    clusterImage,
    clusterHoverImage,
    clusterOffset,
    clusterHoverOffset,
    clusterSize,
    clusterHoverSize,
    placemarkImage,
    placemarkHoverImage,
    placemarkActiveImage,
    placemarkOffset,
    placemarkHoverOffset,
    placemarkActiveOffset,
    placemarkSize,
    placemarkHoverSize,
    placemarkActiveSize,
    pins,
    allPins: pins,
    occupiedAreas,
    zoomControlPosition,
    selected: (dealershipItem == null ? void 0 : dealershipItem.id) || null,
    hovered: null,
    styles,
    mapRef,
    bodyRef
  };
};
export {
  useContainer
};
